import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useAPI from "../../Hooks/useAPI";
import "./UserNotifications.css";
import { useUser } from "../../Context/UserContext";
import { default as Header } from "../../Components/Header2/Header2";
import { default as Footer } from "../../Components/Footer/FooterSmall";
import Input from "../../Components/Input/Input";
import TextArea from "../../Components/TextArea/TextArea";
import "../../Components/Page/Page.css";
import Button2 from "../../Components/Button2/Button2";

const USERLIST_OPTIONS = [
  "Student",
  "Scientist",
  "Engineer",
  "Manufacturer",
  "Academic",
  "Government",
  "Defense",
  "Marketing",
  "Law",
  "Health",
  "Other",
];

interface UserNotificationFormData {
  messagetext: string;
  startdate: string;
  enddate: string;
  userlist: string[];
  rolelist: number[];
  messagefrom: boolean;
}

interface FormErrors {
  messagetext?: string;
  startdate?: string;
  enddate?: string;
  userlist?: string;
}

// Define props for Roles
interface RoleOption {
  id: number;
  name: string;
}

// Define props for User List
interface UserOption {
  id: string;
  name: string;
  roles: number[];
}

export default function UserNotificationsAdd() {
  const navigate = useNavigate();
  const { user, setUser, hasRole } = useUser();
  const {
    getRoles,
    getUsersByRole,
    CreateNotificationMessage,
    CreateUserNotificationMessage,
  } = useAPI();
  const [formData, setFormData] = useState<UserNotificationFormData>({
    messagetext: "",
    startdate: "",
    enddate: "",
    userlist: [],
    rolelist: [],
    messagefrom: false,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [, setError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [useroptions, setUserOption] = useState<UserOption[]>([]);
  const [isSysAdmin, setIsSysAdmin] = useState(hasRole("administrator"));
  const DEFAULT_ROLE_ID = -1;
  const [manualUserIds, setManualUserIds] = useState<string[]>([]);
  const [roleUserIds, setRoleUserIds] = useState<string[]>([]);
  const [excludedUserIds, setExcludedUserIds] = useState<string[]>([]);

  // Fetch roles
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await getRoles();

        const rolesWithDefault = [
          ...response,
          { id: -1, name: "Default User (No Role Assigned)" },
        ];

        setRoles(rolesWithDefault);
      } catch (error) {
        console.error("Error loading roles:", error);
      }
    };

    fetchRoles();
  }, []);

  // Fetch userlist
  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const response = await getUsersByRole(); // get ALL

        setUserOption(
          response.data.map((u: any) => ({
            id: u.id,
            name: u.name,
            roles: (u.roles ?? []).filter((r: any) => r != null),
          })),
        );
      } catch (error) {
        console.error("Error loading users:", error);
      }
    };

    fetchAllUsers();
  }, []);

  const getUsersFromRoles = (roleIds: number[]) => {
    return useroptions
      .filter((user) => {
        const userRoles = (user.roles ?? []).filter((r) => r != null);

        const matchesNormalRole = userRoles.some((r) => roleIds.includes(r));

        const matchesDefaultRole =
          roleIds.includes(DEFAULT_ROLE_ID) && userRoles.length === 0;

        return matchesNormalRole || matchesDefaultRole;
      })
      .map((u) => u.id);
  };

  useEffect(() => {
    if (formData.rolelist.length === 0) {
      setRoleUserIds([]);
      return;
    }

    const matched = getUsersFromRoles(formData.rolelist);
    setRoleUserIds(matched);
  }, [formData.rolelist, useroptions]);

  useEffect(() => {
    const combined = Array.from(new Set([...manualUserIds, ...roleUserIds]));

    setFormData((prev) => ({
      ...prev,
      userlist: combined,
    }));
  }, [manualUserIds, roleUserIds]);

  const fieldRefs = {
    messagetext: useRef<HTMLInputElement>(null),
    startdate: useRef<HTMLInputElement>(null),
    enddate: useRef<HTMLInputElement>(null),
    userlist: useRef<HTMLDivElement>(null),
    rolelist: useRef<HTMLDivElement>(null),
    messagefrom: useRef<HTMLInputElement>(null),
  };

  const isFormValid = (): boolean => {
    return true;
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    // Message validation
    if (!formData.messagetext.trim()) {
      newErrors.messagetext = "Message is required";
      isValid = false;
    }

    // Start Date validation
    if (!formData.startdate) {
      newErrors.startdate = "Start Date is required";
      isValid = false;
    }

    // End Date validation
    if (!formData.enddate) {
      newErrors.enddate = "End Date is required";
      isValid = false;
    }

    // User list validation
    if (formData.userlist.length === 0) {
      newErrors.userlist = "Please select at least one user";
      isValid = false;
    }

    setErrors(newErrors);

    const firstErrorKey = Object.keys(newErrors)[0] as keyof typeof fieldRefs;

    const el = fieldRefs[firstErrorKey]?.current;

    if (el) {
      const headerOffset = 90; // header height
      const elementPosition = el.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });

      el.focus?.();
    }

    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return; // extra safety

    setIsSubmitting(true);

    // Clear general error
    setErrors((prev) => ({ ...prev, general: undefined }));

    try {
      if (!validateForm()) {
        setIsSubmitting(false);
        return;
      }

      //combine manually checked off users and users from roles
      const allSelectedUserIds = Array.from(
        new Set([...manualUserIds, ...roleUserIds]),
      ).filter((id) => !excludedUserIds.includes(id));

      const formDataToSubmit = {
        ...formData,
        userlist: allSelectedUserIds,
      };

      if (!user?.id) {
        throw new Error("User ID is required to create notification");
      }

      const msgresponse = await CreateNotificationMessage(
        formDataToSubmit.messagetext,
        1,
        isSysAdmin ? "Admin" : user.id,
        true,
        formDataToSubmit.startdate,
        formDataToSubmit.enddate,
      );
      const messageid = msgresponse.data.rows[0].insert_messageref;

      const batchSize = 10;

      if (messageid > 0) {
        for (let i = 0; i < allSelectedUserIds.length; i += batchSize) {
          const batch = allSelectedUserIds.slice(i, i + batchSize);

          await Promise.all(
            batch.map((userId) =>
              CreateUserNotificationMessage(
                userId,
                messageid,
                isSysAdmin ? "Admin" : user.id,
              ),
            ),
          );
        }
      }

      const addedMessage = "Notification has been sent ";
      alert(addedMessage);
      // Redirect to home page
      navigate("/user-management/UserNotifications");
    } catch (err) {
      console.error("Error submitting form:", err);
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;

    // Handle checkbox safely
    if (e.target instanceof HTMLInputElement && e.target.type === "checkbox") {
      const { checked } = e.target;

      if (name === "rolelist") {
        setFormData((prev) => {
          const val = Number(value);

          const updatedRoles = checked
            ? [...prev.rolelist, val]
            : prev.rolelist.filter((id) => id !== val);

          return {
            ...prev,
            rolelist: updatedRoles,
          };
        });
        return;
      }

      if (name === "userlist") {
        setManualUserIds((prev) =>
          checked ? [...prev, value] : prev.filter((id) => id !== value),
        );

        setExcludedUserIds((prev) => {
          if (!checked && roleUserIds.includes(value)) {
            // user is part of a role → exclude them
            return [...prev, value];
          } else {
            // user manually checked → remove from exclusions
            return prev.filter((id) => id !== value);
          }
        });

        return;
      }
    }

    // All non-checkbox inputs (including textarea)
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectAllUsers = () => {
    const allIds = useroptions.map((u) => u.id);

    setManualUserIds(allIds); // force everything selected manually
    setRoleUserIds([]); // optional: prevents duplication confusion

    setFormData((prev) => ({
      ...prev,
      userlist: allIds,
    }));
  };

  const handleClearAllUsers = () => {
    setManualUserIds([]);
    setRoleUserIds([]);

    setFormData((prev) => ({
      ...prev,
      userlist: [],
      rolelist: [], // IMPORTANT: also uncheck role filters
    }));
  };

  const handleSelectAllRoles = () => {
    const allRoleIds = roles.map((r) => r.id);

    setFormData((prev) => ({
      ...prev,
      rolelist: allRoleIds,
      userlist: getUsersFromRoles(allRoleIds),
    }));
  };

  const handleClearAllRoles = () => {
    setFormData((prev) => ({
      ...prev,
      rolelist: [],
      userlist: [],
    }));
  };

  return (
    <div className="generalPage">
      <Header />

      <div className="pageContainer">
        <div className="usernotificationsadd-table-container">
          <form onSubmit={handleSubmit}>
            <div className="usernotificationsadd-form-group">
              <table className="usernotificationsadd-table">
                <thead>
                  <tr>
                    <th
                      colSpan={6}
                      className="usernotificationsadd-table-section_header"
                    >
                      Add a new User Notification
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <th>Message *</th>
                    <td id="profile-messagetext">
                      <TextArea
                        label=""
                        id="messagetext"
                        placeholder="Enter message"
                        required={true}
                        rows={8}
                        cols={20}
                        name="messagetext"
                        value={formData.messagetext}
                        onChange={handleChange}
                      />{" "}
                      {errors.messagetext && (
                        <div className="field-error">{errors.messagetext}</div>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th>Send Message From</th>
                    <td id="profile-messagefrom">
                      <div className="usernotification-radio-group">
                        <label>
                          <input
                            type="radio"
                            name="messagefrom"
                            value="true"
                            checked={isSysAdmin === true}
                            onChange={() => setIsSysAdmin(true)}
                          />
                          ICEGems System Admistrator
                        </label>

                        <label>
                          <input
                            type="radio"
                            name="messagefrom"
                            value="false"
                            checked={isSysAdmin === false}
                            onChange={() => setIsSysAdmin(false)}
                          />
                          {user?.firstname + " " + user?.lastname}
                        </label>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th>Start Date *</th>
                    <td id="profile-startdate">
                      {" "}
                      <div className="dateinputs">
                        <Input
                          label=""
                          type="date"
                          id="startdate"
                          placeholder="Enter start date"
                          required={true}
                          name="startdate"
                          value={formData.startdate}
                          onChange={handleChange}
                        />
                      </div>
                      {errors.startdate && (
                        <div className="field-error">{errors.startdate}</div>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th>End Date *</th>
                    <td id="profile-enddate">
                      {" "}
                      <div className="dateinputs">
                        <Input
                          label=""
                          type="date"
                          id="enddate"
                          ref={fieldRefs.enddate}
                          placeholder="Enter end date"
                          required={true}
                          name="enddate"
                          value={formData.enddate}
                          onChange={handleChange}
                        />
                      </div>
                      {errors.enddate && (
                        <div className="field-error">{errors.enddate}</div>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <th>
                      Roles<br></br> (Narrow User List by Role)
                    </th>
                    <td id="profile-rolelist">
                      <div className="checkbox-group">
                        <div className="checkbox-select-actions">
                          <button
                            type="button"
                            className="checkbox-select-actions-btn"
                            onClick={handleSelectAllRoles}
                          >
                            Select All
                          </button>
                          /
                          <button
                            type="button"
                            className="checkbox-select-actions-btn"
                            onClick={handleClearAllRoles}
                          >
                            Clear All
                          </button>
                        </div>
                        <div ref={fieldRefs.rolelist}>
                          {roles.map((option) => (
                            <div key={option.id} className="checkbox-item">
                              <label htmlFor={`rolelist-${option.id}`}>
                                <input
                                  type="checkbox"
                                  id={`rolelist-${option.id}`}
                                  name="rolelist"
                                  value={option.id}
                                  checked={formData.rolelist.includes(
                                    option.id,
                                  )}
                                  onChange={handleChange}
                                  className="checkboxCtrl"
                                />{" "}
                                {option.name}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th>
                      User List<br></br> (Select at least one)
                    </th>
                    <td id="profile-userlist">
                      <div className="checkbox-group">
                        <div className="checkbox-select-actions">
                          <button
                            type="button"
                            className="checkbox-select-actions-btn"
                            onClick={handleSelectAllUsers}
                          >
                            Select All
                          </button>
                          /
                          <button
                            type="button"
                            className="checkbox-select-actions-btn"
                            onClick={handleClearAllUsers}
                          >
                            Clear All
                          </button>
                        </div>

                        <div ref={fieldRefs.userlist}>
                          {Array.isArray(useroptions) &&
                            useroptions.map((option) => (
                              <div
                                key={option.id}
                                className={`checkbox-item ${
                                  roleUserIds.includes(option.id)
                                    ? "highlight"
                                    : ""
                                }`}
                              >
                                <label htmlFor={`userlist-${option.id}`}>
                                  <input
                                    type="checkbox"
                                    id={`userlist-${option.id}`}
                                    name="userlist"
                                    value={option.id}
                                    checked={
                                      (manualUserIds.includes(option.id) ||
                                        roleUserIds.includes(option.id)) &&
                                      !excludedUserIds.includes(option.id)
                                    }
                                    className="checkboxCtrl"
                                    onChange={handleChange}
                                  />
                                  {option.name}
                                </label>
                              </div>
                            ))}
                        </div>
                      </div>

                      {errors.userlist && (
                        <div className="field-error">{errors.userlist}</div>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={2} className="usernotification-submit-button">
                      <Button2
                        name="submit"
                        text={
                          isSubmitting
                            ? "Submitting..."
                            : "Add User Notification"
                        }
                        type="button"
                        classname="submit-btn add-usernotification"
                        disabled={isSubmitting}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
}
